import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, getTrackByFn, InputObservable } from '@y42/angular/util';
import {
  BehaviorSubject,
  debounceTime,
  delay,
  filter,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  ChatAvatar,
  ChatRoomMessageUIModel,
  ChatRoomMessageUIPayload,
  MentionUser,
} from '../../models/chat-room.model';

@UntilDestroy()
@Component({
  selector: 'y42-chat-room',
  templateUrl: './chat-room.component.html',
  styleUrls: ['./chat-room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatRoomComponent implements AfterViewInit {
  @Input() author?: ChatAvatar;
  @Input() isResolveable = false;

  @Input() mentionUsers: MentionUser[] = [];

  @Input()
  @InputObservable()
  conversation: ChatRoomMessageUIModel[] = [];
  conversation$!: Observable<ChatRoomMessageUIModel[]>;

  private readonly moveToMessageId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set moveToMessageId(val: string | undefined) {
    this.moveToMessageId$.next(val);
  }

  @Output() commented = new EventEmitter<ChatRoomMessageUIPayload>();
  @Output() edited = new EventEmitter<{
    messageId: string;
    editedMessage: ChatRoomMessageUIPayload;
  }>();
  @Output() deleted = new EventEmitter<ChatRoomMessageUIModel['id']>();
  @Output() resolved = new EventEmitter<void>();
  @Output() moveToMessageIdConsumed = new EventEmitter<void>();

  @ViewChild('chatMessages', { static: false, read: ElementRef }) chatMessages?: ElementRef;

  readonly trackByID = getTrackByFn<ChatRoomMessageUIModel>('id');

  private stickyToBottom = true;

  private readonly highlight$ = new BehaviorSubject<string | undefined>(undefined);

  public readonly state = connectState(this, {
    highlight: this.highlight$.pipe(
      switchMap((highlight) => {
        if (highlight === undefined) {
          return of(undefined).pipe(delay(100)); // we need to emit it with a small delay
        }

        return of(highlight);
      }),
    ),
  });

  ngAfterViewInit() {
    this.conversation$
      .pipe(
        startWith([]),
        pairwise(),
        delay(200),
        tap(([prev, next]) => {
          const forceScrollToBottom =
            !!next.length && next.length - prev.length > 0 && next[next.length - 1]?.isAuthorMessage;
          this.scrollToBottom(forceScrollToBottom);
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.moveToMessageId$
      .pipe(
        filter((messageId) => !!messageId),
        switchMap((moveToMessageId) => {
          return this.conversation$.pipe(
            filter((conversation) => conversation.length > 0),
            map(() => moveToMessageId),
            take(1),
          );
        }),
        debounceTime(500),
        tap((moveToMessageId) => {
          if (!moveToMessageId) {
            return;
          }
          const moveToMessageItem = this.chatMessages?.nativeElement.querySelector(`.message-id-${moveToMessageId}`);
          if (moveToMessageItem) {
            if (moveToMessageItem.scrollIntoViewIfNeeded) {
              moveToMessageItem.scrollIntoViewIfNeeded();
            } else if (moveToMessageItem.scrollIntoView) {
              moveToMessageItem.scrollIntoView();
            }
            this.highlight(moveToMessageId);
          }

          this.moveToMessageId$.next(undefined); // let's wait until setter will set a new value to scroll to...
          this.moveToMessageIdConsumed.emit();
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  onResolved() {
    this.resolved.emit();
  }

  onEdited(editedConfig: { messageId: ChatRoomMessageUIModel['id']; editedMessage: ChatRoomMessageUIPayload }) {
    this.edited.emit(editedConfig);
  }

  onDeleted(id: ChatRoomMessageUIModel['id']) {
    this.deleted.emit(id);
  }

  onNewComment(newMessage: ChatRoomMessageUIPayload) {
    this.stickyToBottom = true;
    this.commented.emit(newMessage);
  }

  chatMessagesScrolled(event: Event) {
    const TO_BOTTOM_MARGIN = 10;
    const element = (event.target || {}) as HTMLElement;
    const { scrollTop, scrollHeight, offsetHeight } = element;

    const userScrolledToBottom = Math.abs(scrollHeight - scrollTop - offsetHeight) < TO_BOTTOM_MARGIN;
    this.stickyToBottom = userScrolledToBottom;
  }

  editPhaseChanged() {
    this.stickyToBottom = false;
  }

  private scrollToBottom(forceScrollToBottom = false) {
    if (this.chatMessages && (this.stickyToBottom || forceScrollToBottom)) {
      this.chatMessages.nativeElement.scrollTop = this.chatMessages.nativeElement.scrollHeight;
    }
  }

  private highlight(messageId: string) {
    this.highlight$.next(messageId);
    this.highlight$.next(undefined);
  }
}
